import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

interface ChartSeries {
  name: string;
  data: (number | null)[];
}

interface Props {
  data: { categories: string[]; series: ChartSeries[] };
  title?: string;
  neighborhoodName?: string;
}

const FONT = "'Montserrat Light', 'Montserrat', sans-serif";
const FONT_SIZE = '11pt';
const FONT_STYLE = { fontFamily: FONT, fontSize: FONT_SIZE, fontWeight: 'normal' as const };

function fmtMillions(val: number): string {
  return `$${Math.round(val / 1_000_000)}M`;
}

export const SalesDistributionChart = React.forwardRef<HighchartsReact.RefObject, Props>(
  ({ data, title = 'Sales Distribution', neighborhoodName = '' }, ref) => {
    const subtitle = `${neighborhoodName} | Comparison of Luxury Full Service and Traditional Condominiums`;
    const traditional = data.series[0]?.data ?? [];
    const luxe = data.series[1]?.data ?? [];

    const options: Highcharts.Options = {
      chart: {
        type: 'column',
        height: 560,
        marginTop: 160,
        style: { fontFamily: FONT, fontSize: FONT_SIZE }
      },
      title: {
        text: title,
        align: 'left',
        style: { fontFamily: "'Montserrat', sans-serif", fontSize: '30pt', fontWeight: '500', color: '#000000' }
      },
      subtitle: {
        text: subtitle,
        align: 'left',
        style: { fontFamily: "'Montserrat', sans-serif", fontSize: '14pt', fontWeight: '500', color: '#66635F' }
      },
      xAxis: {
        categories: data.categories,
        title: { text: 'Year', style: FONT_STYLE },
        labels: { style: FONT_STYLE }
      },
      yAxis: {
        min: 0,
        reversedStacks: false,
        title: { text: undefined },
        labels: { enabled: false }
      },
      legend: {
        itemStyle: FONT_STYLE
      },
      tooltip: {
        style: FONT_STYLE,
        formatter: function (this: any) {
          const val = (this.y as number) ?? 0;
          const total = this.point.total as number;
          return `<b>${this.x}</b><br/>${this.series.name}: ${fmtMillions(val)}<br/>Total: ${fmtMillions(total)}`;
        }
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          pointPadding: 0,
          groupPadding: 0.15,
        }
      },
      series: [
        {
          type: 'column',
          name: 'Traditional',
          color: '#DDE4E6',
          data: traditional,
          dataLabels: {
            enabled: true,
            allowOverlap: true,
            format: '{point.percentage:.1f}%',
            style: { ...FONT_STYLE, textOutline: 'none', color: '#555' }
          }
        },
        {
          type: 'column',
          name: 'Luxe',
          color: '#B6B8AE',
          data: luxe,
          dataLabels: [
            {
              enabled: true,
              allowOverlap: true,
              format: '{point.percentage:.1f}%',
              style: { ...FONT_STYLE, textOutline: 'none', color: '#555' }
            },
            {
              enabled: true,
              verticalAlign: 'top',
              y: -30,
              crop: false,
              overflow: 'allow' as const,
              allowOverlap: true,
              formatter: function (this: any) {
                return fmtMillions(this.total as number);
              },
              style: { ...FONT_STYLE, textOutline: 'none', color: '#333' }
            }
          ]
        }
      ],
      credits: { enabled: false }
    };

    return <HighchartsReact ref={ref} highcharts={Highcharts} options={options} />;
  }
);
